import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChartPie,
  MessageSquareMore,
  NotebookPen,
} from "lucide-react";
import platformHeroImage from "../assets/platform-hero.png";

const PLATFORM_FEATURES = [
  {
    title: "보장분석",
    description: [
      "주요 8대 보장 및 약 80여 개의 보장 항목 중 고객이 원하는 보장만 선택하여, 최적의 상품을 확인할 수 있습니다.",
      "빅데이터를 기반한 분석 결과로 보다 효율적인 맞춤 상담이 가능하며, 필요에 따라 분석 자료를 고객에게 모바일로 전송할 수 있습니다.",
    ],
    icon: MessageSquareMore,
  },
  {
    title: "상품설계",
    description: [
      "제휴 보험사의 전산에 들어가서 각각 설계할 필요없이 굿리치상품설계를 통해 각 보험사의 상품을 쉽고 빠르게 설계할 수 있습니다.",
    ],
    icon: NotebookPen,
  },
  {
    title: "보험추천",
    description: [
      "11개 손해보험사 15개 생명보험사의 보험 상품 3,078건을 빅데이터화 하여 보험을 추천해드리는 플랫폼입니다.",
      "고객의 나이, 성별, 운전 여부 등 기본 정보를 바탕으로 고객의 원하는 요건에 따라 최적의 보험 상품을 추천할 수 있습니다.",
    ],
    icon: ChartPie,
  },
];

function FeatureIcon({ icon: Icon }) {
  return (
    <div className="relative flex h-[68px] w-[68px] items-center justify-center sm:h-[78px] sm:w-[78px] md:h-[84px] md:w-[84px] lg:h-[92px] lg:w-[92px]">
      <div className="absolute inset-0 rounded-full bg-[linear-gradient(135deg,rgba(255,150,1,0.24),rgba(24,45,95,0.18))] blur-[1px]" />
      <div className="relative flex h-full w-full items-center justify-center rounded-full border border-[rgba(24,45,95,0.12)] bg-white/92">
        <Icon
          className="h-[46%] w-[46%] text-secondary sm:h-[48%] sm:w-[48%] lg:h-[50%] lg:w-[50%]"
          strokeWidth={1.55}
        />
      </div>
    </div>
  );
}

function FeatureRow({ item, showBorder = true }) {
  return (
    <article
      className={[
        "grid items-start gap-4 py-6 sm:gap-6 sm:py-8 lg:grid-cols-[1fr_108px] lg:gap-8 lg:py-10",
        showBorder ? "border-t border-[#dddddd]" : "",
      ].join(" ")}
    >
      <div>
        <h2 className="text-[24px] font-extrabold leading-[1.3] tracking-[-0.04em] text-secondary sm:text-[28px] md:text-[30px] lg:text-[32px]">
          {item.title}
        </h2>

        <div className="mt-3 space-y-1.5 sm:mt-4 sm:space-y-2">
          {item.description.map((line, idx) => (
            <p
              key={idx}
              className="text-[15px] leading-[1.72] tracking-[-0.02em] text-gray-700 sm:text-[17px] md:text-[18px] lg:text-[20px] lg:leading-[1.8]"
            >
              {line}
            </p>
          ))}
        </div>
      </div>

      <div className="order-first flex justify-start lg:order-none lg:justify-end lg:self-center">
        <FeatureIcon icon={item.icon} />
      </div>
    </article>
  );
}

export default function PlannerPlatformPage() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-white">
      <section className="mx-auto max-w-[1400px] px-4 pb-12 pt-7 sm:px-6 sm:pb-16 sm:pt-10 md:px-10 md:pb-20 md:pt-12 lg:px-12 lg:pb-24 lg:pt-14 xl:px-16">
        <div className="grid items-start gap-7 lg:grid-cols-[260px_1fr] lg:gap-10 xl:grid-cols-[340px_1fr] xl:gap-12">
          <div className="self-start flex w-full flex-row items-center gap-2.5 sm:gap-3 md:gap-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex shrink-0 items-center text-secondary"
              aria-label="이전 페이지로 이동"
            >
              <ChevronLeft className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8" />
            </button>

            <h1 className="text-[28px] font-extrabold leading-[1.2] tracking-[-0.04em] text-secondary sm:text-[34px] md:text-[40px] lg:text-[42px] xl:text-[46px]">
              설계사 플랫폼
            </h1>
          </div>

          <div>
            <div className="max-w-[920px]">
              <p className="mt-1 text-[15px] leading-[1.75] text-gray-700 sm:mt-2 sm:text-[17px] md:text-[18px] lg:text-[20px] lg:leading-[1.8]">
                굿리치플래너는 설계사의 업무효율을 제고하는 플랫폼입니다.
                <br className="hidden sm:block" />
                보장분석, 상품설계, 보험추천 등 설계사가 고객을 위한 최적의
                대안을 클릭 몇 번으로 쉽고 편하게 찾을 수 있을 뿐만 아니라 인수
                심사, 계약 체결, 고객 관리도 가능합니다.
              </p>
            </div>

            <div className="mt-6 overflow-hidden bg-[#dcdcdc] sm:mt-8 md:mt-9 sm:rounded-none">
              <img
                src={platformHeroImage}
                alt="설계사 플랫폼 소개 이미지"
                className="h-auto w-full object-cover"
              />
            </div>

            <div className="mb-8 mt-3 sm:mb-10 sm:mt-4 md:mt-5 lg:mb-14 lg:mt-6">
              {PLATFORM_FEATURES.map((item, idx) => (
                <FeatureRow
                  key={item.title}
                  item={item}
                  showBorder={idx !== 0}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
